import PropTypes from 'prop-types';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import FieldTypes from '../../../enums/FieldTypes';
import getDeliveryAddressAttribute from '../../delivery-address/getDeliveryAddressAttribute';
import Field2 from '../../form/Field2';
import FieldWrapper from '../../form/FieldWrapper/FieldWrapper';
import isUsingArtemisStyles from '../../selectors/isUsingArtemisStyles';
import Width from '../../style-helper/Width';
import doMetric from '../../tracking/bigQueryMetricRecorder';
import Track from '../../utils/tracking';
import MockInput from '../ui/MockInput/MockInput';
import EmptyOption from './EmptyOption';
import * as required from './field-validation/required-validators';
import useStyles from '../../form/SelectField.styles';

export function CountrySelect(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme, width: Width.full});
  
  const handleChange = (event) => {
    props.onChange(event);
    try {
      if (props.addressType === 'delivery') {
        doMetric('selectDeliveryAddress', props.getDeliveryAddressAttribute());
      }
    } catch (error) {
      Track.track(error);
    }
  };
  
  const id = `${props.addressType}-country`;
  const name = `${props.fieldNamePrefix + props.addressType}Address.country`;
  const countryOptions = props.countries.map((country, index) => (
    <option key={index + 1} value={country.code}>
      {country.name}
    </option>
  ));
  countryOptions.unshift(<EmptyOption key={0} />);
  
  let validate = [required.basic];
  
  if (props.restrictDeliveryCountry) {
    validate = [
      ...validate,
      props.restrictDeliveryCountry,
    ];
  }
  
  const hasSingleCountry = props.countries.length === 1;
  
  return (
    <Field2
      disabled={props.disabled}
      key={`${props.addressType}Address.country`}
      fieldContainer={props.fieldContainer}
      definedField={{name: 'country', validate}}
      value={props.value}
      onChange={handleChange}
      nameOnForm={name}
    >
      {(onChange, onBlur, onFocus, errorText) => (
        <FieldWrapper
          label="checkout.delivery.address.field.label.country.text"
          required
          htmlFor={id}
          wrappedClassName="country"
          errorText={errorText}
          fieldType={hasSingleCountry ? FieldTypes.MOCK : null}
        >
          {hasSingleCountry ? (
            <MockInput
              id={id}
              dataTestSuffix={`${props.addressType}-country`}
              displayValue={props.countries[0].name}
              inputValue={props.countries[0].code}
              errorText={errorText}
              validate={onBlur}
            />
          ) : (
            <select
              id={id}
              data-test={`${props.addressType}-country-field`}
              className={`${classes.selectField} ${errorText ? 'error-block-v2' : ''}`}
              name={name}
              disabled={props.disabled}
              onChange={event => onChange(event)}
              onBlur={onBlur}
              onFocus={onFocus}
              value={props.value}
            >
              {countryOptions}
            </select>
          )}
        </FieldWrapper>
      )}
    </Field2>
  );
}

const mapStateToProps = state => ({
  isUsingArtemisStyles: isUsingArtemisStyles(state),
});

CountrySelect.propTypes = {
  addressType: PropTypes.oneOf(['delivery', 'billing']).isRequired,
  countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  fieldContainer: PropTypes.shape({
    deregisterField: PropTypes.func.isRequired,
    registerField: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    validate: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  getDeliveryAddressAttribute: PropTypes.func.isRequired,
  
  // optional
  restrictDeliveryCountry: PropTypes.func,
  fieldNamePrefix: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  isUsingArtemisStyles: PropTypes.bool,
};

CountrySelect.defaultProps = {
  fieldNamePrefix: '',
};

export default connect(mapStateToProps, {getDeliveryAddressAttribute})(CountrySelect);
